import { useEffect, useState } from 'react';
import type { AsyncFlow, AsyncFlowState } from '../../contract/async-flow';
import type { FlowSubscription } from '../../contract/subscription';
import { isServer } from '../../utils/isServer';
import { skipToken, type SkipToken } from '../skipToken';

interface AsyncRenderSubscriptionEntry {
    subscription: FlowSubscription;
    // Незавершённые render-попытки: commit снимает только свой token и не затрагивает suspended-соседей.
    renderTokens: Map<string | symbol, AsyncRenderSubscriptionToken>;
    timeoutId: ReturnType<typeof setTimeout> | null;
    // Максимальный запрошенный timeout: меньший timeout другого consumer не сокращает retention.
    retentionMs: number;
    // Retention deadline после settle; в pending его нет — запись держит выполняющийся запрос.
    releaseAt: number | null;
    isPending: boolean;
    // Инвалидирует tokens от render-попыток, начавшихся до нового pending-цикла.
    generation: number;
}

interface AsyncRenderSubscriptionToken {
    flow: AsyncFlow<unknown>;
    entry: AsyncRenderSubscriptionEntry;
    owner: string | symbol;
    sawPending: boolean;
    generation: number;
    // Таймер жёсткого предела этой render-попытки. Взводится, только когда запись сама отпустит
    // token позже его дедлайна. Ход таймера не зависит от состояния потока: двигать срок может
    // только новый acquire того же owner.
    hardTimeoutId: ReturnType<typeof setTimeout> | null;
    // Дедлайн жёсткого предела. Повторный acquire двигает его только вперёд, поэтому
    // acquire с меньшим timeout не сокращает уже запрошенный срок.
    hardReleaseAt: number;
}

interface AsyncRenderSubscriptionOwnerRegistration {
    id: string;
    owner: symbol;
    orphanTimeoutId: ReturnType<typeof setTimeout> | null;
    // Дедлайн регистрации: не раньше hard deadline token'а этого owner, иначе Suspense retry
    // после исчезновения регистрации получил бы новый owner и потерял старый token.
    orphanReleaseAt: number;
}

// Render-попытка, которая так и не дошла до commit (React отбросил hook state при initial-mount
// Suspense retry, либо поток бесконечно чередует pending и settle), не может держать запись дольше
// этого срока от своего acquire. Пользовательский timeout больше срока продлевает его.
const HARD_RETENTION_TIMEOUT_MS = 10 * 60_000;

// setTimeout принимает 32-битную знаковую задержку: больший срок он превратил бы примерно в 1 мс,
// поэтому длинные дедлайны взводятся чанками по этому пределу.
const MAX_TIMEOUT_DELAY_MS = 2_147_483_647;

// Сколько раз acquire перезапускается, пока pull переставляет registry под ним.
const MAX_ACQUIRE_ATTEMPTS = 5;

/**
 * Взводит таймер на остаток до дедлайна и перевзводит его, пока дедлайн не наступил: так
 * переживают 32-битный предел setTimeout длинные сроки, а сдвиг дедлайна вперёд подхватывается
 * без пересоздания таймера. `setTimeoutId` получает id текущего чанка (или `null`, когда чанк
 * отработал), `onDeadline` вызывается после наступления дедлайна.
 */
function armDeadlineTimer(
    getReleaseAt: () => number | null,
    setTimeoutId: (timeoutId: ReturnType<typeof setTimeout> | null) => void,
    onDeadline: () => void
): void {
    const releaseAt = getReleaseAt();
    if (releaseAt === null) {
        return;
    }

    setTimeoutId(
        setTimeout(
            () => {
                setTimeoutId(null);
                const currentReleaseAt = getReleaseAt();
                if (currentReleaseAt === null) {
                    return;
                }
                if (currentReleaseAt - Date.now() > 0) {
                    armDeadlineTimer(getReleaseAt, setTimeoutId, onDeadline);
                    return;
                }
                onDeadline();
            },
            Math.min(Math.max(0, releaseAt - Date.now()), MAX_TIMEOUT_DELAY_MS)
        )
    );
}

// Можно безопасно хранить подписки в глобальной переменной, так как на сервере они не создаются
const asyncRenderSubscriptions = new Map<AsyncFlow<unknown>, AsyncRenderSubscriptionEntry>();
const asyncRenderSubscriptionTokensByOwner = new Map<string | symbol, AsyncRenderSubscriptionToken>();
// Ключ — id из `useId`. Допущение: id при гидрации (`:R…`) и при клиентском маунте (`:r…`) не
// пересекаются, а одновременно гидрируемые корни задают разные `identifierPrefix`.
const asyncRenderSubscriptionOwnersById = new Map<string, AsyncRenderSubscriptionOwnerRegistration>();
const asyncRenderSubscriptionOwnerRegistrations = new Map<symbol, AsyncRenderSubscriptionOwnerRegistration>();

function removeOwnerRegistration(registration: AsyncRenderSubscriptionOwnerRegistration): void {
    if (registration.orphanTimeoutId !== null) {
        clearTimeout(registration.orphanTimeoutId);
        registration.orphanTimeoutId = null;
    }

    if (asyncRenderSubscriptionOwnersById.get(registration.id) === registration) {
        asyncRenderSubscriptionOwnersById.delete(registration.id);
    }
    if (asyncRenderSubscriptionOwnerRegistrations.get(registration.owner) === registration) {
        asyncRenderSubscriptionOwnerRegistrations.delete(registration.owner);
    }
}

function removeOwnerRegistrationByOwner(owner: string | symbol): void {
    if (typeof owner === 'symbol') {
        const registration = asyncRenderSubscriptionOwnerRegistrations.get(owner);
        if (registration) {
            removeOwnerRegistration(registration);
        }
    }
}

/** Двигает дедлайн регистрации только вперёд: более ранний срок её не укорачивает. */
function armOwnerRegistrationOrphanTimer(
    registration: AsyncRenderSubscriptionOwnerRegistration,
    releaseAt: number
): void {
    if (registration.orphanTimeoutId !== null && releaseAt <= registration.orphanReleaseAt) {
        return;
    }

    registration.orphanReleaseAt = Math.max(registration.orphanReleaseAt, releaseAt);
    if (registration.orphanTimeoutId !== null) {
        clearTimeout(registration.orphanTimeoutId);
    }
    armDeadlineTimer(
        () => registration.orphanReleaseAt,
        timeoutId => {
            registration.orphanTimeoutId = timeoutId;
        },
        () => removeOwnerRegistration(registration)
    );
}

/**
 * Продлевает регистрацию owner до hard deadline его token'а: пока token живёт по собственному
 * таймеру, Suspense retry должен получить тот же owner, иначе снять этот token уже будет некому.
 * Вызывается вместе со взводом таймера: token без таймера уходит с записью не позже своего
 * дедлайна, а базовые десять минут регистрации взводит `getOrCreateOwnerRegistration`.
 */
function extendOwnerRegistrationLifetime(owner: string | symbol, releaseAt: number): void {
    if (typeof owner !== 'symbol') {
        return;
    }

    const registration = asyncRenderSubscriptionOwnerRegistrations.get(owner);
    if (registration) {
        armOwnerRegistrationOrphanTimer(registration, releaseAt);
    }
}

function getOrCreateOwnerRegistration(id: string): AsyncRenderSubscriptionOwnerRegistration {
    const existing = asyncRenderSubscriptionOwnersById.get(id);
    if (existing) {
        armOwnerRegistrationOrphanTimer(existing, Date.now() + HARD_RETENTION_TIMEOUT_MS);
        return existing;
    }

    const registration: AsyncRenderSubscriptionOwnerRegistration = {
        id,
        owner: Symbol(id),
        orphanTimeoutId: null,
        orphanReleaseAt: 0,
    };
    armOwnerRegistrationOrphanTimer(registration, Date.now() + HARD_RETENTION_TIMEOUT_MS);
    asyncRenderSubscriptionOwnersById.set(id, registration);
    asyncRenderSubscriptionOwnerRegistrations.set(registration.owner, registration);
    return registration;
}

/**
 * Превращает React id в owner, стабильный между render-попытками до первого commit: Suspense retry
 * с тем же id получает owner из реестра. На сервере реестр не используется, owner локальный.
 */
export function useAsyncRenderSubscriptionOwner(id: string): symbol {
    const [owner] = useState(() =>
        isServer() ? Symbol('async-render-subscription') : getOrCreateOwnerRegistration(id).owner
    );

    useEffect(() => {
        // После первого commit hook state уже стабилен и registry больше не нужен. Если commit
        // не случится из-за Suspense, registration удалит hard deadline или cleanup token'а.
        removeOwnerRegistrationByOwner(owner);

        return () => {
            removeOwnerRegistrationByOwner(owner);
            // Unmount мог случиться во время Suspense-update: у token'а той render-попытки нет
            // парного cleanup-эффекта, снять его может только lifecycle owner.
            releaseSupersededAsyncRenderSubscription(owner, null);
        };
    }, [owner]);

    return owner;
}

/**
 * Устанавливает таймер на остаток до `releaseAt`.
 * Благодаря этому повторные acquire не создают новых setTimeout/clearTimeout — только сдвигают deadline.
 */
function armTimer(flow: AsyncFlow<unknown>, entry: AsyncRenderSubscriptionEntry): void {
    armDeadlineTimer(
        () => entry.releaseAt,
        timeoutId => {
            entry.timeoutId = timeoutId;
        },
        () => releaseAsyncRenderSubscription(flow)
    );
}

function clearTimer(entry: AsyncRenderSubscriptionEntry): void {
    if (entry.timeoutId !== null) {
        clearTimeout(entry.timeoutId);
        entry.timeoutId = null;
    }
    entry.releaseAt = null;
}

function scheduleAfterSettle(flow: AsyncFlow<unknown>, entry: AsyncRenderSubscriptionEntry): void {
    entry.releaseAt = Math.max(entry.releaseAt ?? 0, Date.now() + entry.retentionMs);
    if (entry.timeoutId === null) {
        armTimer(flow, entry);
    }
}

/**
 * Взводит жёсткий предел token'а, если запись сама уже не снимет его вовремя: у pending-записи
 * retention остановлен, а settled-запись с общим `releaseAt` позже дедлайна token'а (например,
 * когда её retention поднял consumer с большим `keepRenderSubscriptionFor`) отпустила бы его
 * слишком поздно. Дедлайн здесь не пересчитывается: иначе поток, бесконечно чередующий pending
 * и settle, продлевал бы предел на каждом переходе.
 */
function syncTokenHardTimer(token: AsyncRenderSubscriptionToken): void {
    const { releaseAt } = token.entry;
    if (token.hardTimeoutId !== null || (releaseAt !== null && releaseAt <= token.hardReleaseAt)) {
        return;
    }

    armDeadlineTimer(
        () => token.hardReleaseAt,
        timeoutId => {
            token.hardTimeoutId = timeoutId;
        },
        () => releaseExpiredToken(token)
    );
    extendOwnerRegistrationLifetime(token.owner, token.hardReleaseAt);
}

/** Пересматривает необходимость таймеров после сдвига `releaseAt` записи. */
function syncEntryHardTimers(entry: AsyncRenderSubscriptionEntry): void {
    for (const token of entry.renderTokens.values()) {
        syncTokenHardTimer(token);
    }
}

function clearTokenHardTimer(token: AsyncRenderSubscriptionToken): void {
    if (token.hardTimeoutId !== null) {
        clearTimeout(token.hardTimeoutId);
        token.hardTimeoutId = null;
    }
}

/**
 * Снимает token, переживший свой жёсткий предел. Общую подписку освобождает только последний
 * оставшийся token: у соседних render-попыток свои сроки, и они продолжают её удерживать.
 */
function releaseExpiredToken(token: AsyncRenderSubscriptionToken): void {
    if (!removeToken(token)) {
        return;
    }
    removeOwnerRegistrationByOwner(token.owner);

    if (asyncRenderSubscriptions.get(token.flow) === token.entry && token.entry.renderTokens.size === 0) {
        releaseAsyncRenderSubscription(token.flow);
    }
}

function removeToken(token: AsyncRenderSubscriptionToken): boolean {
    if (token.entry.renderTokens.get(token.owner) !== token) {
        return false;
    }

    clearTokenHardTimer(token);
    token.entry.renderTokens.delete(token.owner);
    if (asyncRenderSubscriptionTokensByOwner.get(token.owner) === token) {
        asyncRenderSubscriptionTokensByOwner.delete(token.owner);
    }
    return true;
}

/**
 * Регистрирует token render-попытки. Предыдущий token того же owner в этой же записи заменяется,
 * а его дедлайн переносится на новый token, если был позже. Собственный таймер новому token'у
 * нужен, только когда запись переживёт его дедлайн: при обычном retention (короче предела)
 * settled-token снимет commit, а если commit не случится — retention записи, поэтому таймер
 * на каждый рендер был бы мусором, который тут же снимает commit.
 */
function createToken(
    flow: AsyncFlow<unknown>,
    entry: AsyncRenderSubscriptionEntry,
    owner: string | symbol,
    sawPending: boolean,
    timeoutMs: number
): AsyncRenderSubscriptionToken {
    const previous = entry.renderTokens.get(owner);
    const hardReleaseAt = Math.max(
        previous?.hardReleaseAt ?? 0,
        Date.now() + Math.max(HARD_RETENTION_TIMEOUT_MS, timeoutMs)
    );
    const token: AsyncRenderSubscriptionToken = {
        flow,
        entry,
        owner,
        sawPending,
        generation: entry.generation,
        hardTimeoutId: null,
        hardReleaseAt,
    };

    if (previous) {
        clearTokenHardTimer(previous);
    }
    syncTokenHardTimer(token);

    entry.renderTokens.set(owner, token);
    asyncRenderSubscriptionTokensByOwner.set(owner, token);
    return token;
}

/**
 * Реагирует на материализованное состояние потока: pending останавливает retention, settle
 * запускает retention deadline. Жёсткие пределы render-попыток эти переходы не двигают.
 * Состояние читается без pull — по контракту `subscribe` завершение и старт вычисления
 * доходят до listener'а и без `getSnapshot()`.
 */
function handleObservedSnapshot(
    flow: AsyncFlow<unknown>,
    entry: AsyncRenderSubscriptionEntry,
    snapshot: AsyncFlowState<unknown> | undefined
): void {
    const isPending = snapshot?.status === 'pending';
    if (isPending && !entry.isPending) {
        entry.generation += 1;
    }
    entry.isPending = isPending;
    if (isPending) {
        clearTimer(entry);
    } else if (entry.releaseAt === null) {
        scheduleAfterSettle(flow, entry);
    }
    // В pending retention записи остановлен, поэтому дальше tokens может снять только собственный
    // жёсткий предел; тем, кого создал settled-рендер, таймер взводится здесь.
    syncEntryHardTimers(entry);
}

/**
 * Обрабатывает commit/unmount render-попытки и снимает её token. Пока остаются tokens
 * других render-попыток, token от pending render не может снять общую запись после settle:
 * она может принадлежать ещё не повторённому Suspense-render другого consumer.
 */
function releaseCommittedAsyncRenderSubscription(
    flow: AsyncFlow<unknown>,
    token: AsyncRenderSubscriptionToken | null
): void {
    if (
        !token ||
        asyncRenderSubscriptions.get(flow) !== token.entry ||
        token.entry.renderTokens.get(token.owner) !== token
    ) {
        return;
    }
    removeToken(token);
    removeOwnerRegistrationByOwner(token.owner);

    if (token.entry.renderTokens.size === 0) {
        releaseAsyncRenderSubscription(flow);
        return;
    }

    const snapshot = flow.getCachedSnapshot();
    if (snapshot?.status === 'pending') {
        handleObservedSnapshot(flow, token.entry, snapshot);
        return;
    }

    if (token.generation === token.entry.generation && !token.sawPending) {
        releaseAsyncRenderSubscription(flow);
    }
}

/**
 * Снимает token предыдущей render-попытки consumer при смене flow или переходе на skipToken.
 * Effect для старой попытки после Suspense может так и не появиться, поэтому lifecycle привязан к owner.
 */
function releaseSupersededAsyncRenderSubscription(owner: string | symbol, nextFlow: AsyncFlow<unknown> | null): void {
    const token = asyncRenderSubscriptionTokensByOwner.get(owner);
    if (!token || token.flow === nextFlow) {
        return;
    }

    if (!removeToken(token)) {
        return;
    }
    if (nextFlow === null) {
        removeOwnerRegistrationByOwner(token.owner);
    }
    if (token.entry.renderTokens.size === 0 && asyncRenderSubscriptions.get(token.flow) === token.entry) {
        releaseAsyncRenderSubscription(token.flow);
    }
}

/**
 * Достраивает acquire на уже существующей записи: продлевает retention по состоянию потока
 * и выдаёт token render-попытки. `snapshot` читает вызывающий — pull делается ровно один раз.
 */
function acquireOnEntry(
    flow: AsyncFlow<unknown>,
    entry: AsyncRenderSubscriptionEntry,
    owner: string | symbol,
    timeoutMs: number,
    snapshot: AsyncFlowState<unknown>
): AsyncRenderSubscriptionToken {
    entry.retentionMs = Math.max(entry.retentionMs, timeoutMs);
    const sawPending = snapshot.status === 'pending';
    handleObservedSnapshot(flow, entry, snapshot);
    if (!sawPending) {
        entry.releaseAt = Math.max(entry.releaseAt ?? Date.now() + entry.retentionMs, Date.now() + timeoutMs);
        if (entry.timeoutId === null) {
            armTimer(flow, entry);
        }
        // Продлённый retention может пережить дедлайны уже выданных tokens.
        syncEntryHardTimers(entry);
    }
    return createToken(flow, entry, owner, sawPending, timeoutMs);
}

/**
 * Реентерабельный acquire того же owner на другом потоке уже занял owner-registry. Продолжать
 * начатый acquire нельзя: `createToken` перезапишет там запись, и снять реентерабельный token
 * через owner будет некому — приостановленный consumer потеряет свой запрос.
 */
function hasOwnerLeftFlow(owner: string | symbol, flow: AsyncFlow<unknown>): boolean {
    const ownerToken = asyncRenderSubscriptionTokensByOwner.get(owner);
    return ownerToken !== undefined && ownerToken.flow !== flow;
}

/**
 * Одна попытка acquire. `null` означает, что pull реентерабельно поменял состояние registry
 * под ногами и попытку нужно повторить на актуальном состоянии. При `canRestart: false`
 * попытка всегда доводит acquire до token'а.
 */
function acquireAttempt(
    flow: AsyncFlow<unknown>,
    timeoutMs: number,
    owner: string | symbol,
    canRestart: boolean
): AsyncRenderSubscriptionToken | null {
    releaseSupersededAsyncRenderSubscription(owner, flow);

    const existing = asyncRenderSubscriptions.get(flow);
    if (existing) {
        // Render — законная точка pull: компонент всё равно читает поток, чтобы отрендериться.
        const snapshot = flow.getSnapshot();
        // Pull мог синхронно позвать listener'ов, а те — реентерабельно снять запись потока
        // (token лёг бы в запись, уже отцепленную от Map и без подписки) или увести owner.
        if (canRestart && (asyncRenderSubscriptions.get(flow) !== existing || hasOwnerLeftFlow(owner, flow))) {
            return null;
        }
        return acquireOnEntry(flow, existing, owner, timeoutMs, snapshot);
    }

    let entry: AsyncRenderSubscriptionEntry | undefined;
    const subscription = flow.subscribe(() => {
        if (!entry || asyncRenderSubscriptions.get(flow) !== entry) {
            return;
        }
        handleObservedSnapshot(flow, entry, flow.getCachedSnapshot());
    });
    const snapshot = flow.getSnapshot();

    if (canRestart && hasOwnerLeftFlow(owner, flow)) {
        subscription.unsubscribe();
        return null;
    }

    // subscribe и getSnapshot могут синхронно позвать чужого подписчика, а тот — реентерабельно
    // сделать acquire того же потока. Его запись уже лежит в Map: перезапись бросила бы живую
    // подписку с таймерами, которую не найдёт даже cleanupFlows.
    const reentrant = asyncRenderSubscriptions.get(flow);
    if (reentrant) {
        subscription.unsubscribe();
        return acquireOnEntry(flow, reentrant, owner, timeoutMs, snapshot);
    }

    const sawPending = snapshot.status === 'pending';
    entry = {
        subscription,
        renderTokens: new Map(),
        timeoutId: null,
        retentionMs: timeoutMs,
        releaseAt: sawPending ? null : Date.now() + timeoutMs,
        isPending: sawPending,
        generation: 0,
    };
    asyncRenderSubscriptions.set(flow, entry);
    if (entry.releaseAt !== null) {
        armTimer(flow, entry);
    }
    return createToken(flow, entry, owner, sawPending, timeoutMs);
}

export function acquireAsyncRenderSubscription(
    flow: AsyncFlow<unknown>,
    timeoutMs: number,
    owner: string | symbol = Symbol('async-render-subscription')
): AsyncRenderSubscriptionToken {
    let token: AsyncRenderSubscriptionToken | null = null;
    // Перезапуск возможен только после реентерабельной правки registry внутри pull, поэтому
    // поток, который её не делает, укладывается в первую попытку, а обычная реентерабельность —
    // во вторую. Поток, меняющий состояние на каждом pull, крутил бы цикл бесконечно: последняя
    // попытка идёт без перезапуска и всегда возвращает token.
    for (let attempt = 1; token === null; attempt++) {
        token = acquireAttempt(flow, timeoutMs, owner, attempt < MAX_ACQUIRE_ATTEMPTS);
    }
    return token;
}

export function releaseAsyncRenderSubscription(flow: AsyncFlow<unknown>): void {
    const entry = asyncRenderSubscriptions.get(flow);
    if (!entry) {
        return;
    }

    if (entry.timeoutId !== null) {
        clearTimeout(entry.timeoutId);
    }
    entry.subscription.unsubscribe();
    asyncRenderSubscriptions.delete(flow);
    for (const token of entry.renderTokens.values()) {
        clearTokenHardTimer(token);
        removeOwnerRegistrationByOwner(token.owner);
        if (asyncRenderSubscriptionTokensByOwner.get(token.owner) === token) {
            asyncRenderSubscriptionTokensByOwner.delete(token.owner);
        }
    }
    entry.renderTokens.clear();
}

export function cleanupAsyncRenderSubscriptions(): void {
    for (const flow of Array.from(asyncRenderSubscriptions.keys())) {
        releaseAsyncRenderSubscription(flow);
    }
    for (const registration of Array.from(asyncRenderSubscriptionOwnerRegistrations.values())) {
        removeOwnerRegistration(registration);
    }
}

/**
 * Версия {@link useRenderSubscription} для {@link AsyncFlow}: удерживает подписку на flow во
 * время render-фазы и после неё. Pending-подписка не освобождается чужим commit и живёт до
 * фактического повторного render+commit после settle, но не дольше текущего safety deadline. Парный хук —
 * {@link useAsyncRenderSubscriptionCleanup} — должен быть вызван ПОСЛЕ постоянной подписки
 * (например, после `useSyncExternalStore`).
 *
 * Зачем нужен:
 * Обычный безусловный release на commit небезопасен: глобальная запись может удерживать pending-flow
 * для другого компонента, который приостановлен и ещё не дошёл до commit. Поэтому, пока остаются
 * tokens других render-попыток, запись освобождает только commit/unmount token текущей generation,
 * который был создан уже в settled-состоянии (`!token.sawPending`). Token, созданный до нового
 * pending-цикла, не может освободить его generation.
 *
 * Как работает:
 * - Render-фаза: создаёт подписку `flow.subscribe(...)` (выполняет функцию render→mount-мостика)
 *   и сохраняет её в модульном `Map` (не в стейте компонента, т.к. стейт теряется при Suspense).
 * - Состояние потока хук читает на render через `getSnapshot()`, а в listener'е — только через
 *   `getCachedSnapshot()`: по контракту `subscribe` старт и завершение вычисления доходят до
 *   подписчика без pull, поэтому уведомление никогда не запускает вычисление.
 * - Пока flow pending, retention-таймер не идёт: подписка удерживает выполняющийся запрос. После settle
 *   начинается safety deadline.
 * - Жёсткий предел: token живёт до позднейшего из двух дедлайнов — `max(10 минут, timeoutMs)` от
 *   своего acquire и дедлайна заменённого token того же owner, — в каком бы состоянии ни был поток;
 *   по истечении срока снимается только он, а общая подписка — когда снят последний token. Двигает
 *   срок только повторный acquire того же owner, который заменяет token, и только вперёд, поэтому
 *   acquire с меньшим timeout его не сокращает; поток, бесконечно чередующий pending и settle, его
 *   не двигает. Собственный таймер предела взводится, только когда запись не снимет token раньше
 *   сама: в pending её retention остановлен, а в settled общий `releaseAt` может оказаться позже
 *   дедлайна token'а — его поднимает consumer с большим timeout, вплоть до `Infinity`. При обычном
 *   retention (короче предела) таймера нет: token снимет commit или retention записи. Предел на
 *   token, а не на запись: при клиентском маунте повтор после Suspense получает новый `useId`, а
 *   с ним новый owner, и общий предел новые попытки продлевали бы старым, а `renderTokens` рос бы
 *   без границ.
 * - Повторный acquire двигает deadline только вперёд (`max`), поэтому меньший timeout другого
 *   компонента не сокращает время жизни общей записи.
 * - Выход из pending сам по себе запись не освобождает: React retry может быть отложен.
 *   Актуальный settled-retry освобождает запись вместе с tokens предыдущих pending-попыток.
 *
 * @param flow - AsyncFlow для подписки или `skipToken` для пропуска
 * @param timeoutMs - Safety-таймаут в миллисекундах после settle
 * @param owner - Стабильный между render-попытками идентификатор одного consumer
 * @returns Token текущей render-попытки (или `null`), который нужно без изменений передать в
 * {@link useAsyncRenderSubscriptionCleanup}
 *
 * @internal
 */
export function useAsyncRenderSubscription(
    flow: AsyncFlow<unknown> | SkipToken,
    timeoutMs: number,
    owner: string | symbol = Symbol('async-render-subscription')
): AsyncRenderSubscriptionToken | null {
    if (flow !== skipToken && timeoutMs > 0 && !isServer()) {
        return acquireAsyncRenderSubscription(flow, timeoutMs, owner);
    }
    releaseSupersededAsyncRenderSubscription(owner, null);
    return null;
}

/**
 * На commit/unmount помечает текущую render-попытку завершённой. Если незавершённых попыток
 * больше нет, временная подписка освобождается: постоянная подписка уже создана. Если рядом
 * есть suspended-попытка, её token удерживает общую запись до retry либо safety deadline.
 *
 * Вызывать ОБЯЗАТЕЛЬНО после `useSyncExternalStore` (или иного механизма, создающего постоянную
 * подписку) — иначе release useEffect отработает раньше, чем будет создана постоянная подписка,
 * и между ними появится зазор с нулем подписчиков, в котором источник может выкинуть данные.
 *
 * Если соответствующего acquire не было (`skipToken` или `timeoutMs=0`), `token` равен `null`
 * и вызов безопасен.
 *
 * @param flow - AsyncFlow подписки или `skipToken`
 * @param token - Результат парного вызова {@link useAsyncRenderSubscription}
 *
 * @internal
 */
export function useAsyncRenderSubscriptionCleanup(
    flow: AsyncFlow<unknown> | SkipToken,
    token: AsyncRenderSubscriptionToken | null
): void {
    useEffect(() => {
        if (flow !== skipToken) {
            releaseCommittedAsyncRenderSubscription(flow, token);
        }

        return () => {
            if (flow !== skipToken) {
                releaseCommittedAsyncRenderSubscription(flow, token);
            }
        };
    });
}
